import { ComponentRef, Inject, Injectable } from '@angular/core';
import { CookieService } from "ngx-cookie-service";
import {
  GoogleAnalyticsInitializer, GoogleAnalyticsRouterInitializer,
  GoogleAnalyticsService, GtagFn, IGoogleAnalyticsCommand, IGoogleAnalyticsRoutingSettings,
  IGoogleAnalyticsSettings, NGX_GOOGLE_ANALYTICS_ROUTING_SETTINGS_TOKEN,
  NGX_GOOGLE_ANALYTICS_SETTINGS_TOKEN, NGX_GTAG_FN
} from "ngx-google-analytics";
import { DOCUMENT } from "@angular/common";
import { environment } from "../../environments/environment";
import { UserTrackingData } from "../models/user.model";
import { HttpClient } from "@angular/common/http";
import { catchError, map, Observable, of, tap, throwError } from "rxjs";
import { ConfigService } from "./config.service";
import { ResponseModel } from '../models/response.model';


@Injectable({
  providedIn: 'root'
})
export class TrackingHelperService {

  private appComponentRef: ComponentRef<any> | null = null;

  constructor(
    private http: HttpClient,
    private cookieService: CookieService,
    private configService: ConfigService,
    private gaService: GoogleAnalyticsService,
    @Inject(NGX_GOOGLE_ANALYTICS_SETTINGS_TOKEN) private googleAnalyticsSettingsToken: IGoogleAnalyticsSettings,
    @Inject(NGX_GOOGLE_ANALYTICS_ROUTING_SETTINGS_TOKEN) private googleAnalyticsRoutingSettingsToken: IGoogleAnalyticsRoutingSettings,
    @Inject(NGX_GTAG_FN) private googleTagFunction: GtagFn,
    @Inject(DOCUMENT) private document: Document,
    private configs: ConfigService
  ) { }

  public setAppComponentRef(ref: ComponentRef<any>): void {
    this.appComponentRef = ref;
  }

  public initializeGoogleAnalytics(): void {
    if (this.appComponentRef && environment.GA_TRACKING) {

      const affiliateOptions = this.getAffiliateOptions();

      this.googleAnalyticsSettingsToken.initCommands = [
        {
          command: 'js',
          values: [new Date()]
        },
        {
          command: 'set',
          values: [affiliateOptions]
        },
        {
          command: 'config',
          values: [this.googleAnalyticsSettingsToken.trackingCode]
        }
      ];


      GoogleAnalyticsInitializer(this.googleAnalyticsSettingsToken, this.googleTagFunction, this.document)()
        .then(() => {
          if (this.appComponentRef) {
            GoogleAnalyticsRouterInitializer(this.googleAnalyticsRoutingSettingsToken, this.gaService)(this.appComponentRef);
          }
        })
        .catch((error: any) => {
          console.error('Error initializing Google Analytics', error);
        });
    }
  }


  setCookiesFromQueryParams(): void {
    const urlParams = new URLSearchParams(window.location.search);
    // btag=cx_35514_416668

    const strictCheck = (varName: string): boolean =>
      urlParams.has(varName) && urlParams.get(varName)?.trim() !== "";

    if (strictCheck("t")) {
      const paramValue = urlParams.get("t");
      if (paramValue !== null) {
        this.cookieService.set("tra_tracker", paramValue, { path: "/", expires: 31536000 })
      }
    }
    if (strictCheck("id")) {
      const paramValue = urlParams.get("id");
      if (paramValue !== null) {
        this.cookieService.set("tra_tracker", "00000_" + paramValue, { path: "/", expires: 31536000 })
      }
    }
    if (strictCheck("tracker")) {
      const paramValue = urlParams.get("tracker");
      if (paramValue !== null) {
        this.cookieService.set("tra_tracker", paramValue, { path: "/", expires: 31536000 })
      }
    }
    if (strictCheck("btag")) {
      const paramValue = urlParams.get("btag");
      if (paramValue !== null) {
        this.cookieService.set("tra_btag", paramValue)
      }
    }

  }


  trackEventFromMessage(e: MessageEvent): void {
    switch (e.data?.action) {
      //embedded iframe messages from Rival
      case 'USER_UPDATE_BALANCE':
        //from rival games in iframes + also from keepAlive(balance)
        this.gaService.event('balance', undefined, undefined, undefined, undefined, { user_balance: e.data?.balance, ...this.getAffiliateOptions() });
        break;

      case "GAMES_START_GAME":
        //in rival iframes when its loaded
        this.gaService.event('play', undefined, undefined, undefined, undefined, { game_id: e.data?.id, ...this.getAffiliateOptions() });
        break;

      default:
        break;
    }
    //other events are sent in components
  }


  getTracker(): string | undefined {
    //TODO: max 127 char long, domain at the very end
    let tracker = this.cookieService.get('tra_tracker');

    //fix trackers on GP brands (whenever we use them, they change to the good version)
    if (!this.configs.isRivalCasino) {
      //by Rick - just not does what we want
      // console.log(Object.entries(replacementMap).map(([badTracker,goodTracker])=> tracker.replace(badTracker,goodTracker)));
      const replacementMap = {
        "US_List": "CP-541762",
        "ET-March_2026": "CP-639873"
      }
      for (const [badTracker, goodTracker] of Object.entries(replacementMap)) {
        tracker = tracker.replace(badTracker, goodTracker)
      }
    }
    // console.log(tracker)
    return tracker;
  }

  getBannerTag(): string | undefined {
    return this.cookieService.get('tra_btag');
  }

  getAffiliate() {
    const tracker = this.getTracker();
    return tracker ? tracker.split("_")[0] : undefined
  }

  getAffiliateOptions() {
    const affiliate = this.getAffiliate();
    return affiliate ? { campaign_id: affiliate, affiliate_id: affiliate } : {}
  }

  sendTrackingData(data: UserTrackingData): Observable<any> {
    return this.http.post('https://gp.0x6e.com/user-login', data).pipe(
      tap((response: any) => {
        // flip from rival to GP when response has {"merchant_id": 1234,"token": "ksdfyujgkahsjgkfjhgqwd"}
        if (response && 'token' in response && typeof response.token === 'string'
          && 'merchant_id' in response && typeof response.merchant_id === 'number') {
          this.configService.flipAPI({ token: response.token, merchant_id: response.merchant_id });
        }
      }),
      catchError(() => of(null)) // swallow error silently
    );
  }

}


